import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { BrandLogo } from "@/components/BrandLogo";
import { consumeNextPath } from "@/lib/nextPath";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const { data, error: signInError } = await authClient.signIn.email({ email, password });
    setLoading(false);
    if (signInError) {
      setError(authErrorMessage(signInError, "Could not sign in."));
      return;
    }
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      // Leave the stashed next-path in place — TwoFactorPage consumes it.
      navigate({ to: "/two-factor" });
      return;
    }
    navigate({ to: consumeNextPath() ?? "/" });
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <BrandLogo className="auth-logo" size={22} />
        <h1 className="auth-title">Sign in</h1>
        <p className="auth-subtitle">Break free from your IDE.</p>

        <form className="panel" onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <div className="field-label-row">
              <label className="label" htmlFor="password">Password</label>
              <Link className="field-label-action" to="/forgot-password">Forgot password?</Link>
            </div>
            <input
              id="password"
              className="input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error && <div className="error-text auth-form-error" role="alert">{error}</div>}
          <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="auth-footer">
          New to Rivets? <Link to="/register">Create account</Link>
        </p>
      </div>
    </div>
  );
}
